import React, { useState, useEffect, memo } from 'react';
import { Card, CardContent, Typography, Chip, IconButton, Box, Menu, MenuItem, Collapse } from '@mui/material';
import { PiPencilSimple, PiTrash, PiDotsThreeVertical, PiCaretDown, PiCaretUp, PiFile } from 'react-icons/pi';
import RenameJobDialog from './RenameJobDialog';
import ExecutionMonitor from './ExecutionMonitor';
import { DocumentThumbnail } from './DocumentThumbnail';
import ProgressBar from './ProgressBar';

interface JobCardProps {
  job: {
    jobId: string;
    executionArn?: string;
    status: string;
    createdAt: string;
    startedAt?: string;
    step?: number;
    v2Progress?: {
      completedSteps: number;
      totalSteps: number;
      currentStepName?: string;
      currentStepIndex?: number;
      stepNames?: string[];
    };
    displayName?: string;
    practice?: string;
    workflows?: string[];
    number?: string;
    input?: {
      practice: string;
      workflows: string[];
      number: string;
      input_file_s3_uris?: string[];
    };
  };
  onPause: (jobId: string) => void;
  onResume: (jobId: string) => void;
  onCancel: (jobId: string) => void;
  onRename: (jobId: string, newName: string) => void;
  onDelete: (jobId: string) => void;
  isSelected?: boolean;
  /** Whether to show document thumbnail */
  showThumbnail?: boolean;
}

const JobCard = memo(function JobCard({ job, onPause, onResume, onCancel, onRename, onDelete, showThumbnail = true }: JobCardProps) {
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [now, setNow] = useState(Date.now());

  // get input file URIs - check both V1 and V2 formats
  const v1Files = job.input?.input_file_s3_uris || [];
  const v2Raw = (job.input as any)?.workflow_data?.input_files || (job.input as any)?.workflow_data?.files || [];
  const v2Files = v2Raw.map((f: any) => typeof f === 'string' ? f : (f?.s3_uri || '')).filter(Boolean);
  const inputFiles = v1Files.length > 0 ? v1Files : v2Files;
  const firstPdfFile = inputFiles.find((uri: string) => uri.toLowerCase().endsWith('.pdf'));

  // extract clean filenames from S3 URIs for display on the card
  const inputFileNames = inputFiles
    .map(uri => uri.split('/').pop() || '')
    .filter(Boolean);

  // Update timer every second for running jobs
  useEffect(() => {
    if (job.status === 'RUNNING') {
      const interval = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(interval);
    }
  }, [job.status]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'RUNNING': return 'primary';
      case 'SUCCEEDED':
      case 'COMPLETED': return 'success';
      case 'FAILED': return 'error';
      case 'WAITING_FOR_INPUT': return 'info';
      case 'QUEUED': return 'warning';
      default: return 'default';
    }
  };

  const getDisplayStatus = (status: string) => {
    return status === 'SUCCEEDED' ? 'COMPLETED' : status;
  };

  const getProgress = () => {
    if (job.status === 'SUCCEEDED' || job.status === 'COMPLETED') return 100;
    if (job.status === 'FAILED') return 0;
    // use real V2 progress when available
    if (job.v2Progress && job.v2Progress.totalSteps > 0) {
      return Math.round((job.v2Progress.completedSteps / job.v2Progress.totalSteps) * 100);
    }
    return ((job.step || 0) / 5) * 100;
  };

  return (
    <Card sx={{ mb: 1, cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {/* Document Thumbnail */}
          {showThumbnail && firstPdfFile && (
            <Box sx={{ flexShrink: 0 }}>
              <DocumentThumbnail 
                s3Uri={firstPdfFile}
                width={60}
                height={78}
              />
            </Box>
          )}
          
          {/* Job Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 1 }}>
                {job.displayName || job.number || job.input?.number || 'Unknown'}
              </Typography>
              <Chip label={getDisplayStatus(job.status)} color={getStatusColor(job.status)} size="small" />
            </Box>
            
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '0.75rem' }}>
              {job.practice || job.input?.practice} • {(job.workflows || job.input?.workflows)?.map((w: string) => w.replace(/__v2$/, '')).join(', ')}
            </Typography>

            {inputFileNames.length > 0 && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 0.25 }}>
                <PiFile size={11} style={{ verticalAlign: 'middle', marginRight: 3, marginTop: -1 }} />
                {inputFileNames.length <= 2
                  ? inputFileNames.join(', ')
                  : `${inputFileNames[0]} +${inputFileNames.length - 1} more`}
              </Typography>
            )}

            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
              {new Date(job.createdAt).toLocaleString()}
              {job.status === 'RUNNING' && (
                <> • {Math.floor((now - new Date(job.createdAt).getTime()) / 1000)}s</>
              )}
            </Typography>
            
            {/* Only show progress bar when collapsed - ExecutionMonitor shows its own when expanded */}
            {job.status === 'RUNNING' && !expanded && (
              <Box sx={{ mt: 1 }}>
                <ProgressBar value={getProgress()} height={100} />
              </Box>
            )}
          </Box>
          
          {/* Actions */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}>
              <PiDotsThreeVertical size={18} />
            </IconButton>
            <IconButton size="small" sx={{ mt: 'auto' }}>
              {expanded ? <PiCaretUp size={18} /> : <PiCaretDown size={18} />}
            </IconButton>
          </Box>
        </Box>
        
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem onClick={() => { setShowRenameDialog(true); setMenuAnchor(null); }}>
            <PiPencilSimple size={18} style={{ marginRight: 8 }} /> Rename
          </MenuItem>
          <MenuItem onClick={() => { onDelete(job.jobId); setMenuAnchor(null); }} sx={{ color: 'error.main' }}>
            <PiTrash size={18} style={{ marginRight: 8 }} /> Delete
          </MenuItem>
        </Menu>
        
        <RenameJobDialog
          open={showRenameDialog}
          currentName={job.displayName || job.number || job.input?.number || 'Unknown'}
          onClose={() => setShowRenameDialog(false)}
          onRename={(newName) => onRename(job.jobId, newName)}
        />
      </CardContent>
      
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 2, pt: 0 }} onClick={(e) => e.stopPropagation()}>
          <ExecutionMonitor executionArn={job.executionArn || job.jobId} />
        </Box>
      </Collapse>
    </Card>
  );
});

export default JobCard;